<template>
  <div class="w-100 p-2 mt-2">
    <page-header :name="'Platform wallet'">
      <b-icon-person-fill class="mr-2"></b-icon-person-fill>
      {{ $t('page.title.reported_errors') }}
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('title.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.category"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="category"
                  :label="$t('filters.label.error_category')"
                  :placeholder="$t('filters.placeholder.error_category')">
              </filter-select>
            </div>
            <div class="col-4">
              <filter-select-with-operator
                  :filter-query="filterQuery"
                  :operator-options="selectOptions.containOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.contains')}"
                  :value-options="locale"
                  :value-multiple="false"
                  options-label="label"
                  options-track-by="label"
                  filter-name="questionLocale"
                  :label="$t('filters.label.language')">
              </filter-select-with-operator>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
      </template>
    </page-card>
    <page-card>
      <template v-slot:header>
        {{ $t('title.list') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

          <div class="table-responsive">
            <b-table
                :busy="isBusy"
                striped show-empty
                :empty-text="$t('table.no_results')"
                hover
                head-variant="dark"
                outlined
                :sort-by.sync="orderColumn"
                :sort-desc.sync="orderDirection"
                :no-local-sorting="true"
                :items="items"
                :fields="fields">

              <template #cell(questionId)="data">
                <div>
                  {{ data.value | shortId }}
                </div>
              </template>

              <template #cell(questionLocale)="data">
                <template>
                  <img :src="require(`@/assets/flags/${data.value.toLowerCase()}.svg`)" class="table-flags mr-1">
                  {{ data.value.toUpperCase() }}
                </template>
              </template>

              <template #cell(category)="data">
                <div>
                  {{ $t(data.value) }}
                </div>
              </template>

              <template #cell(lastReportedDate)="data">
                <div>
                  {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
                </div>
              </template>

              <template #cell(details)="data">
                <div class="table-action--cell">
                  <b-button v-if="data" @click="openModal(data.item)"
                            variant="primary" size="sm"
                  >
                    {{ $t('table.btn.reported_details') }}
                  </b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{ $t('state.loading') }}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
      </template>
      <grid-pagination :total-count="totalCount"
                       :per-page="perPage"
                       :current-page="currentPage"
                       @load-items="paginationReload"></grid-pagination>
    </page-card>
  </div>
</template>

<script>

import axios from "axios";
import GridMixin from "../../../../components/grid/GridMixin";
import PageHeader from "../../../../components/page/PageHeader";
import GridPagination from "../../../../components/grid/grid-pagination/GridPagination";
import PageCard from "../../../../components/page/PageCard";
import FilterSelect from "../../../../components/grid/filters/FilterSelect";
import EditQuestionModal from "../../EditQuestionModal";
import FilterSelectWithOperator from "../../../../components/grid/filters/FilterSelectWithOperator";


export default {
  mixins: [GridMixin],
  components: {
    GridPagination,
    FilterSelectWithOperator,
    FilterSelect,
    PageHeader,
    PageCard
  },
  data() {
    return {
      gridGetUrl: '/api/question/wrong-question-report',
      categoryGetUrl: '/api/question/question-category',
      showDeleted: false,
      orderColumn: 'lastReportedDate',
      orderDirection: false,
      selectOptions: {
        status: [
          {value: 20, text: this.$t('option.published')},
          {value: 10, text: this.$t('option.draft')},
          {value: 30, text: this.$t('option.deleted')}
        ],
        type: [
          {value: 'text', text: this.$t('option.text')},
          {value: 'image', text: this.$t('option.image')},
          {value: 'sound', text:this.$t('option.sound')},
          {value: 'video', text: this.$t('option.video')},
        ],
        containOperator: [
          {value: 'eq', text: this.$t('operator.contains')},
          {value: 'neq', text: this.$t('operator.not_contains')},
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        bulkActions: [
          {value: null, text: this.$t('table.bulk.actions')},
          {value: 'delete', text: this.$t('table.bulk.actions.delete')},
        ],
        difficulty: [
          {value: 1, text: this.$t('option.easy')},
          {value: 2, text: this.$t('option.medium')},
          {value: 3, text: this.$t('option.difficult')},
          {value: 4, text: this.$t('option.very_difficult')}
        ],
        lang: [],
        category: [],
      },
      filterOptions: {
        category: [
          {value: 'to_be_verified', text: this.$t('label.status.to_be_verified')},
          {value: 'request_cancelled', text: this.$t('label.status.request_cancelled')}
        ]
      },
      fields: [
        {key: 'questionNumId', label: this.$t('table.header.reported_id'), sortable: true},
        {key: 'questionLocale', label: this.$t('table.header.language')},
        {key: 'countReported', label: this.$t('table.header.countReported'), sortable: true},
        {key: 'countReportCanceled', label: this.$t('table.header.countReportCanceled'), sortable: true},
        {key: 'category', label: this.$t('table.header.reported_status'), sortable: true},
        {key: 'lastReportedDate', label: this.$t('table.header.lastReportedDate'), sortable: true},
        {key: 'details', label: this.$t('table.header.details')},
      ],
      items: [],
    };
  },
  mounted() {
    this.getData(this.categoryGetUrl+'?limit=1000', ['selectOptions', 'category']);
    this.$on('refresh-dict', () => {
      this.loadItems();
    })
  },
  methods: {
    getData: function (url, localStorage) {
      let _that = this;
      axios.get(url)
          .then((resp) => {
            if (localStorage[1] === 'category') {
              _that[localStorage[0]][localStorage[1]] = resp.data.items.map(c => {
                c.translated = this.$t('category.'+c.name);
                return c;
              });
            } else {
              _that[localStorage[0]][localStorage[1]] = resp.data.items;
            }
          })
          .catch((err) => {
            console.log(err)
          })
    },
    openModal(data) {
        this.$store.commit('modal/openModal', {
          parent: this,
          modal: EditQuestionModal,
          modalName: 'createModal',
          data: {id: data.questionId, correctQuestion: data.questionLocale},
          props: {apiKey: this.apiKey, selectOptions: this.selectOptions}
        });
    }
  },
  computed: {
    locale: function () {
      return this.$store.getters["root/locale"]
          .filter(o => o.domainGroup === process.env.VUE_APP_FRONT_GROUP_NAME);
    },
  },
  watch: {}
}
</script>
